/**
 * @file: utils.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { FormState } from '@euler/lib/form/controller';
import { PredefinedDateRanges } from '@euler/model/viewmodel';
import { TaskListSearchParams } from '@euler/services';
import { comparer } from '@euler/utils';
import qs from 'qs';
import {
  getAllCriterionProviders,
  getSearchCriterionProvider,
} from './factory';
import {
  SearchCriteria,
  SearchCriteriaBookmark,
  SearchCriterionType,
} from './types';

export function searchCriteriaEquals(a: SearchCriteria, b: SearchCriteria) {
  return buildSearchCriteriaKey(a) === buildSearchCriteriaKey(b);
}

export function searchFormToCriteria(form: FormState<SearchCriteria>) {
  const criteria: SearchCriteria = {} as any;
  for (const provider of getAllCriterionProviders()) {
    const value = form[provider.type].value;
    (criteria as any)[provider.type] = value as any;
  }
  return criteria;
}

export function searchCriteriaToSearchParams(
  criteria: SearchCriteria,
): TaskListSearchParams {
  const params: TaskListSearchParams = {};
  const orderDateRange = PredefinedDateRanges[criteria.orderDateRange].value();
  params.startDate = orderDateRange.startDate;
  params.endDate = orderDateRange.endDate;
  params.status = criteria.status;
  params.childStatus = undefined;
  params.customerType = criteria.customerType;
  params.customerIsOnSite = criteria.customerIsOnSite;
  params.carwash = criteria.carwash;
  params.tag = criteria.tag?.name;
  params.serviceAgentId = criteria.serviceAgent?.id;
  params.brand = criteria.vehicleBrand?.name;
  params.fuelType = criteria.vehicleFuelType;
  params.mileageRange = criteria.mileageRange;
  return params;
}

export function isSearchParamsValid(params: TaskListSearchParams) {
  return Boolean(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    params.keyword ||
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      params.licensePlateNo ||
      params.startDate != null ||
      params.endDate != null ||
      params.status != null ||
      params.childStatus != null ||
      params.customerType != null ||
      params.customerIsOnSite != null ||
      params.carwash != null ||
      params.tag != null ||
      params.serviceAgentId != null ||
      params.brand != null ||
      params.fuelType != null ||
      params.mileageRange != null,
  );
}

export function buildSearchCriteriaKey(criteria: SearchCriteria) {
  const keys: Array<SearchCriterionType> = Object.keys(criteria)
    .filter(x => (criteria as any)[x] != null)
    .sort(comparer) as any;

  const values = keys.reduce<Record<string, any>>((res, k) => {
    const provider = getSearchCriterionProvider(k);
    res[k] = provider.getKey(criteria[k]);
    return res;
  }, {});

  return qs.stringify(values);
}

export function buildSearchCriteriaBookmark(
  criteria: SearchCriteria,
): SearchCriteriaBookmark {
  const title: string[] = [];
  const tags: string[] = [];

  const def = PredefinedDateRanges[criteria.orderDateRange];
  title.push(def?.text);

  if (criteria.status != null) {
    const provider = getSearchCriterionProvider('status');
    title.push(provider.getDisplayText(criteria.status));
  }

  if (criteria.tag != null) {
    title.push(criteria.tag.name);
  }

  if (criteria.customerType != null) {
    const provider = getSearchCriterionProvider('customerType');
    title.push(provider.getDisplayText(criteria.customerType));
  } else {
    title.push('车辆');
  }

  if (criteria.serviceAgent != null) {
    tags.push(`服务顾问: ${criteria.serviceAgent.name}`);
  }

  if (criteria.vehicleBrand != null) {
    tags.push(`品牌: ${criteria.vehicleBrand.name}`);
  }

  if (criteria.vehicleFuelType != null) {
    tags.push(criteria.vehicleFuelType);
  }

  if (criteria.customerIsOnSite) {
    tags.push('在厂等待');
  }

  if (criteria.carwash) {
    tags.push('洗车');
  }

  if (
    criteria.mileageRange?.start != null ||
    criteria.mileageRange?.end != null
  ) {
    const provider = getSearchCriterionProvider('mileageRange');
    const text = provider.getDisplayText(criteria.mileageRange);
    tags.push(`里程: ${text}`);
  }

  return {
    key: buildSearchCriteriaKey(criteria),
    title: title.join(''),
    tags,
  };
}
